import { useEffect } from "react";

type Options = {
  enabled?: boolean;
  initialDelay?: number;
  pollInterval?: number;
  maxWait?: number;
  stableTicks?: number;
};

export default function useStableHashScroll(options: Options = {}) {
  const {
    enabled = true,
    initialDelay = 300,
    pollInterval = 120,
    maxWait = 4000,
    stableTicks = 3,
  } = options;

  useEffect(() => {
    if (!enabled) return;
    if (typeof window === "undefined") return;
    const hash = window.location.hash;
    if (!hash) return;
    const id = hash.replace(/^#/, "");
    if (!id) return;

    let iv: number | undefined;
    let timeoutId: number | undefined;

    const scrollToIdStable = () => {
      const el = document.getElementById(id);
      if (!el) return;

      const getY = () => el.getBoundingClientRect().top + window.scrollY;
      let lastY = getY();
      // initial attempt
      window.scrollTo({ top: lastY, behavior: "auto" });

      const start = Date.now();
      let stableCount = 0;
      iv = window.setInterval(() => {
        const y = getY();
        if (Math.abs(y - lastY) <= 2) {
          stableCount++;
        } else {
          stableCount = 0;
        }
        lastY = y;

        if (stableCount >= stableTicks || Date.now() - start > maxWait) {
          if (iv) {
            clearInterval(iv);
            iv = undefined;
          }
          window.scrollTo({ top: y, behavior: "auto" });
        }
      }, pollInterval) as unknown as number;
    };

    const schedule = (delay = initialDelay) => {
      if (timeoutId) clearTimeout(timeoutId as number);
      timeoutId = window.setTimeout(
        scrollToIdStable,
        delay,
      ) as unknown as number;
    };

    if (document.readyState === "complete") {
      schedule(Math.max(150, initialDelay));
    } else {
      const onLoad = () => schedule(200);
      window.addEventListener("load", onLoad);
      schedule(500);
    }

    const onHash = () => schedule(200);
    window.addEventListener("hashchange", onHash);

    return () => {
      if (timeoutId) clearTimeout(timeoutId as number);
      if (iv) clearInterval(iv as number);
      window.removeEventListener("hashchange", onHash);
      window.removeEventListener("load", schedule as any);
    };
  }, [enabled, initialDelay, pollInterval, maxWait, stableTicks]);
}
